import { useEffect } from "react";
import { ArrowLeft, Award, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { Navigation } from "../sections/Navigation";
import { Footer } from "../sections/Footer";

const galleryImages = [
  { url: "https://images.unsplash.com/photo-1560130934-590b85fc08e7?w=300&h=300&fit=crop&q=60", alt: "Restaurant interior" },
  { url: "https://images.unsplash.com/photo-1589091637765-cbd0eff73a44?w=300&h=300&fit=crop&q=60", alt: "Artisan pastry" },
  { url: "https://images.unsplash.com/photo-1579684947550-22e945225d9a?w=300&h=300&fit=crop&q=60", alt: "Gourmet pasta" },
  { url: "https://images.unsplash.com/photo-1497636577773-f1231844b336?w=300&h=300&fit=crop&q=60", alt: "Latte art" },
  { url: "https://images.unsplash.com/photo-1579751626657-72bc17010498?w=300&h=300&fit=crop&q=60", alt: "Wood fired pizza" },
  { url: "https://images.unsplash.com/photo-1509710398975-6454dcdf049f?w=300&h=300&fit=crop&q=60", alt: "Fine dining table" },
  { url: "https://images.unsplash.com/photo-1627308595181-fa2b6f5f04e9?w=300&h=300&fit=crop&q=60", alt: "Artisan bread" },
  { url: "https://images.unsplash.com/photo-1776774795092-63a3b45ec1f8?w=300&h=300&fit=crop&q=60", alt: "Table setting" },
];

export default function ChefPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#EEE8DC]">
      <Navigation />

      {/* Chef Hero */}
      <section className="pt-28 pb-16 md:pt-32 md:pb-20 bg-[#EEE8DC]" data-testid="chef-page-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#191646] font-body text-sm tracking-wider uppercase mb-12 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Chef portrait with gold frame */}
            <div className="flex justify-center lg:justify-start">
              <div className="gold-ornamental-frame max-w-[420px]" data-testid="chef-portrait-frame">
                <img loading="lazy"
                  src="https://vinbasha.com/images/vins.png"
                  alt="Chef Vin Basha"
                  className="w-full h-[500px] md:h-[620px] object-cover object-top"
                  data-testid="chef-portrait-image"
                />
              </div>
            </div>

            {/* Chef bio */}
            <div>
              <div className="gold-separator mb-6" />
              <p className="text-[#C9A85A] text-sm tracking-[0.2em] uppercase font-body font-semibold mb-4">
                Meet the Chef
              </p>
              <h1 className="font-heading text-4xl md:text-5xl text-[#191646] font-medium tracking-tight mb-8">
                Chef Vin Basha
              </h1>

              <div className="space-y-6 mb-10">
                <p className="font-subheading text-lg md:text-xl text-[#191646]/80 italic leading-relaxed">
                  At Kiplings, cooking is our way of gathering stories. Slow, generous, and meant to be shared.
                </p>
                <p className="font-subheading text-lg md:text-xl text-[#191646]/80 italic leading-relaxed">
                  Inspired by old-world tables where conversation lingered long after the meal, we cook with curiosity, warmth, and respect for every ingredient. Here, every plate is an invitation to pause, connect, and savour the moment.
                </p>
              </div>

              {/* Achievements */}
              <div className="space-y-4 mb-10">
                {[
                  "MasterChef Telugu Winner 2024",
                  "8+ Years of Culinary Excellence",
                  "International Training & Expertise",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-[#C9A85A] flex items-center justify-center flex-shrink-0">
                      <Award className="text-[#C9A85A]" size={18} />
                    </div>
                    <span className="text-[#191646]/70 font-body">{item}</span>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <div className="border-l-2 border-[#C9A85A] pl-6 space-y-5">
                {[
                  { year: "2024", event: "Won MasterChef Telugu" },
                  { year: "2023", event: "Joined Isthara as Head Chef" },
                  { year: "2020", event: "Pastry Chef at Bakeman Begins" },
                  { year: "2019", event: "Bakery Chef Specialization" },
                ].map((item, i) => (
                  <div key={i} className="relative">
                    <div className="absolute -left-[30px] w-3.5 h-3.5 bg-[#C9A85A] rounded-full" />
                    <p className="text-[#C9A85A] font-body font-semibold text-sm">{item.year}</p>
                    <p className="text-[#191646]/70 font-body text-sm">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery / Follow the Journey */}
      <section className="py-24 md:py-32 bg-[#191646]" data-testid="chef-gallery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#C9A85A] text-sm tracking-[0.2em] uppercase font-body font-semibold mb-3">
              @kiplingsdeli
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight mb-4">
              Follow the Journey
            </h2>
            <p className="text-white/50 font-body max-w-lg mx-auto">
              Share your Kiplings moments with #KiplingsHyderabad
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                data-testid={`gallery-image-${index}`}
                className="relative aspect-square overflow-hidden group cursor-pointer hover-zoom"
              >
                <img loading="lazy" src={image.url} alt={image.alt} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#191646]/0 group-hover:bg-[#191646]/40 transition-colors duration-300 flex items-center justify-center">
                  <Instagram className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={24} />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://www.instagram.com/kiplingsofficial"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-link"
              className="inline-flex items-center gap-3 text-[#C9A85A] hover:text-white font-body font-medium text-sm tracking-[0.15em] uppercase transition-colors duration-300"
            >
              <Instagram size={18} />
              Follow us on Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
